import {
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useRailSelectionOptional } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { ObjectActionMenu } from "@/object-actions";
import type { ResolvedObjectAction } from "@/object-actions/types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

interface RailMultiMenuContextValue {
  openAt: (e: React.MouseEvent) => void;
}

const RailMultiMenuContext = createContext<RailMultiMenuContextValue | null>(
  null,
);

/**
 * Hosts the multi-selection ObjectActionMenu for a rail. Rows call
 * `useRailRowMultiMenuGate` so right-click on a multi-selected row opens
 * this menu (Finder scoping) instead of the single-item kebab.
 */
export function RailMultiSelectionMenuProvider({
  actions,
  children,
}: {
  actions: ResolvedObjectAction[];
  children: ReactNode;
}) {
  const menu = useRailRowContextMenu();
  // The gate only opens this menu for multi-selections, so the count is
  // always ≥ 2 when visible. It heads the menu — the floating selection bar
  // is hidden for now, so this is where the selection size reads.
  const selection = useRailSelectionOptional();
  const selectionCount = selection?.selectionCount ?? 0;

  const openAt = useCallback(
    (e: React.MouseEvent) => {
      menu.onContextMenu(e);
    },
    [menu],
  );

  const value = useMemo(() => ({ openAt }), [openAt]);

  return (
    <RailMultiMenuContext.Provider value={value}>
      {children}
      {actions.length > 0 && (
        <ObjectActionMenu
          actions={actions}
          kind={{ type: "multi-selection", kinds: [] }}
          name="Selection"
          menuController={menu}
          label="Selection"
          header={
            <>
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                {selectionCount} selected
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          }
        />
      )}
    </RailMultiMenuContext.Provider>
  );
}

/**
 * Finder right-click scoping: if this row is part of a multi-selection,
 * open the rail multi menu; otherwise run the row's single-item handler.
 */
export function useRailRowMultiMenuGate(
  rowKey: string,
  singleOnContextMenu?: (e: React.MouseEvent) => void,
) {
  const selection = useRailSelectionOptional();
  const multi = useContext(RailMultiMenuContext);

  return useCallback(
    (e: React.MouseEvent) => {
      if (
        selection &&
        multi &&
        selection.isSelected(rowKey) &&
        selection.selectionCount > 1
      ) {
        multi.openAt(e);
        return;
      }
      singleOnContextMenu?.(e);
    },
    [selection, multi, rowKey, singleOnContextMenu],
  );
}
